import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import type { ExamAttemptTimeDetailsDTO } from '../models/StudentExamViewDTOs';
import { apiRequest } from '@/src/services';
import { Loading, ErrorMessage, SimpleButton } from '@/src/ui';

export default function TimeRemaining() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [attemptDetails, setAttemptDetails] = useState<ExamAttemptTimeDetailsDTO | null>(null);
    const [endTime, setEndTime] = useState<Date | null>(null);

    const { attemptId = '0' } = useParams();

    const [_tick, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => {
            setTick((t) => t + 1);
        }, 500);

        return () => clearInterval(interval);
    }, []);

    const loadTiming = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await apiRequest<ExamAttemptTimeDetailsDTO>(
                'get',
                `assessments/studentExamView/studentExamAttemptTimeDetails/${attemptId}`
            );
            const attemptDetails = res.data;
            setAttemptDetails(attemptDetails);

            if (attemptDetails.timeRemainingSeconds != null) {
                const endTime = new Date();
                endTime.setSeconds(
                    endTime.getSeconds() + (attemptDetails.timeRemainingSeconds ?? 0)
                );
                setEndTime(endTime);
            }
        } catch {
            setError('Failed to load time details.');
        } finally {
            setLoading(false);
        }
    }, [attemptId]);

    useEffect(() => {
        loadTiming();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attemptId]);

    // No time limit
    if (!endTime) return <></>;
    return (
        <div className="assessment-nav-pane flex flex-row py-4 gap-x-4">
            {loading ? (
                <Loading size="sm" />
            ) : attemptDetails ? (
                <div className="exam-timer">
                    {getFormattedTimeRemaining(attemptDetails, endTime)}
                </div>
            ) : null}
            {error && (
                <>
                    <ErrorMessage error={error} />
                    <SimpleButton onClick={loadTiming}>Retry</SimpleButton>
                </>
            )}
        </div>
    );
}

function getFormattedTimeRemaining(
    attemptDetails: ExamAttemptTimeDetailsDTO | null,
    endTime: Date
) {
    if (!attemptDetails) return null;
    if (!attemptDetails.hasExamStarted) return 'Not started';
    if (attemptDetails.isExamCompleted) return 'Time is up';

    const now = new Date();
    if (endTime <= now) return 'Time is up';

    const diff = Math.max(endTime.getTime() - now.getTime(), 0);
    const minutes = Math.floor(diff / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')} remaining`;
}
